import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import type {
  AccessPath,
  AssistantForAnalytics,
  GroupForAnalytics,
  TeammateForAnalytics,
} from '@shared';
import type { UploadOrigin } from '../../../analytics/analytics.events';
// Aliased on purpose: the barrel withholds this symbol because the bare name
// collides with `@angular/forms`' `Validators`, and this file already imports
// `Validator` from there.
import {
  createFileUploadV2Store,
  effectiveUploadCapInMB,
  FileError,
  FileUploadStateFile,
  FileUploadTriggerDirective,
  FileUploadV2Store,
} from '../../shared/directives/file-upload-v2';
import { Validators as UploadValidators } from '../../shared/directives/file-upload-v2/file-upload-v2.helpers';

/**
 * The skill builder's file-upload control value: persisted-shape entries only
 * (`id === the store/document uuid`). The DTO builders read `id`/`name`, the
 * group validators read `name`/length, and the update diff matches
 * `file.id === doc.uuid` — so `uuid` must NEVER appear as a field name here.
 * Re-homed from the deleted V1 `AssistantSaveModalFileUploadState` (minus
 * `isLoading` — loading is store status now).
 */
export interface AssistantBuilderFileValue {
  id: string;
  name: string;
  extension: string;
  size: number;
}

/** A file-upload status that is staged for persistence (in the control value). */
const PERSISTABLE_STATUSES: ReadonlySet<FileUploadStateFile['status']> =
  new Set(['upload-pending', 'upload-ongoing', 'upload-done'] as const);

const isPersistable = (file: FileUploadStateFile): boolean =>
  PERSISTABLE_STATUSES.has(file.status);

/** Stable key for an id set — cheap change detection for onChange gating. */
const idKey = (ids: string[]): string =>
  [...ids].sort((a, b) => a.localeCompare(b)).join('|');

/** A file chip rendered from store state (loading / error styling per status). */
interface ZoneChip {
  id: string;
  name: string;
  fileType: fromSeriousSystem.FileTypesIconsType;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Skill-builder file-upload zone (SB-3325 PR-5): the successor to the V1
 * save-modal CVA component, rebuilt on the shared file-upload-v2 machinery.
 * Same selector-level UX contract (label, picker button, chips with per-file
 * loading, close-to-remove, disabled/read-only) but backed by a
 * `FileUploadV2Store` instead of the global NgRx `assistantSaveModalFiles`
 * feature.
 *
 * Store resolution (Store-lifecycle decision #2): the zone accepts an optional
 * `[uploadStore]` (the compliance shared-store mechanism) and otherwise lazily
 * creates its own via `createFileUploadV2Store(injector)` — there is NO DI
 * provider, so a bare `inject(FileUploadV2Store)` throws in the builder tree by
 * construction.
 */
@Component({
  selector: 'squadbox-assistant-builder-file-upload',
  imports: [
    NgClass,
    MatTooltipModule,
    TranslateModule,
    FileUploadTriggerDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.FileChipComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AssistantBuilderFileUploadComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AssistantBuilderFileUploadComponent,
      multi: true,
    },
  ],
  template: `
    <div class="flex flex-col gap-2 group">
      <!-- Visual caption, not a <label>: the real control is the hidden input
           generated by the trigger directive (no stable id to associate), and
           the picker's visible text is its own accessible name. -->
      <span
        class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5"
        [class]="{
          'text-neutral-400': isDisabled(),
          'text-neutral-700': !isDisabled(),
        }"
      >
        {{ label() }}
        @if (required()) {
          <span class="text-primary-500"> *</span>
        }
      </span>
      @if (resolvedStore(); as store) {
        <button
          type="button"
          squadboxFileUploadTrigger
          [store]="store"
          [accept]="acceptAttribute()"
          [multiple]="multiple()"
          [matTooltip]="supportedFormatsTooltip()"
          matTooltipPosition="above"
          class="
            group
            bg-shades-white
            border hover:border-primary-300 focus:outline-hidden
            rounded-lg pl-4 pr-3 py-2 h-12
            grid grid-cols-[1fr_auto] items-center
            disabled:border-neutral-200 disabled:hover:border-neutral-200 disabled:pointer-events-none
          "
          [class.disabled]="isDisabled()"
          [ngClass]="{
            'border-error-500 hover:border-error-600': currentErrors().length,
            'border-neutral-200 hover:border-primary-300':
              !currentErrors().length,
          }"
          [disabled]="isDisabled()"
        >
          <span
            class="typo-p3 text-neutral-500 group-disabled:text-neutral-400 justify-self-start"
            >{{ placeholder() }}</span
          >
          @if (filePickerButtonIcon() === 'plus') {
            <svg
              sdUseUIBasicIcon="plus"
              class="size-6 shrink-0"
              [class]="{
                'text-neutral-400': isDisabled(),
                'text-neutral-700': !isDisabled(),
              }"
            ></svg>
          } @else {
            <svg
              sdUseArrowIcon="chevron-small-down"
              class="size-6 shrink-0"
              [class]="{
                'text-neutral-400': isDisabled(),
                'text-neutral-700': !isDisabled(),
              }"
            ></svg>
          }
        </button>
      }

      <!-- Chip/error area: only rendered when there are chips or errors, so an
           empty picker is just the 48px button (no reserved blank line below).
           Keeps the field-to-next-element spacing tight per Figma; the reserved
           space (nbsp + negative margins) still positions chips when present. -->
      @if (chips().length || currentErrors().length) {
        <div class="grow">
          <div>
            <div
              class="flex flex-col -mt-1"
              [ngClass]="{
                'justify-between': currentErrors().length,
                'justify-end': !currentErrors().length,
              }"
            >
              @for (message of currentErrors(); track message) {
                <span class="typo-caption text-error-600">{{ message }}</span>
              }
              @if (!currentErrors().length) {
                <!-- Prevents file chips from overlapping the picker button.
                     Only needed here: with no error text, this is the block's
                     only content, and it must reserve one line of height so
                     the chip row below doesn't jump when chips appear. With an
                     error, the message itself already gives the block real
                     content — an nbsp line under a growing message list is
                     unclaimed height with nothing to reserve it FOR. -->
                <p>&nbsp;</p>
              }
            </div>
            <div
              class="flex flex-wrap gap-2 mr-14"
              [ngClass]="{
                '-mt-5': !currentErrors().length,
                'mt-2': currentErrors().length,
              }"
            >
              @for (chip of chips(); track chip.id) {
                <sd-file-chip
                  [filename]="chip.name"
                  [fileType]="chip.fileType"
                  [isLoading]="chip.isLoading"
                  [isError]="chip.isError"
                  [isDisabled]="isDisabled()"
                  rightIcon="close"
                  (rightIconClicked)="onRemove(chip.id)"
                ></sd-file-chip>
              }
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class AssistantBuilderFileUploadComponent
  implements ControlValueAccessor, Validator, OnInit
{
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  // Inputs (selector-level UX contract mirrors the V1 component)
  public readonly label = input<string>(
    this.translate.instant('ASSISTANTS.SAVE_MODAL.FORM.FILES.LABEL') as string,
  );
  public readonly placeholder = input<string>(
    this.translate.instant(
      'ASSISTANTS.SAVE_MODAL.FORM.FILES.PLACEHOLDER',
    ) as string,
  );
  public readonly filePickerButtonIcon = input<'chevron' | 'plus'>('chevron');
  public readonly fileSupportedExtensions = input.required<string[]>();
  public readonly transactionId = input.required<string>();
  public readonly multiple = input<boolean>(true);
  /**
   * `true` ONLY on the trait forms' configuration control, where `skill.md` is
   * the legitimate upload (SB-3515 T25). Everywhere else the reserved name is
   * refused at staging — fail-closed by default, so a zone that never heard of
   * the rule gets the gate rather than the hole.
   */
  public readonly allowReservedSkillMdFilename = input<boolean>(false);
  /**
   * Renders the required marker beside the caption, exactly as `sd-input` does
   * (`input.component.ts:137-140`) — same construct so the two surfaces cannot
   * drift. NOTE the caption here is deliberately NOT a `<label>`, per the
   * comment above it, so this is a marker on a visual caption rather than on a
   * label. Default false, so every skill-builder zone is unchanged; the Edit
   * knowledge modal (SB-3650 / T3) is the only caller that passes a value, and
   * it reads it from the trait descriptor rather than deciding per template.
   */
  public readonly required = input<boolean>(false);
  /**
   * Analytics `from` for uploads staged on this component's store (SB-3650).
   * Defaults to `skill_builder`, the value this component hardcoded before, so
   * every builder zone behaves identically; the Edit knowledge modal is the
   * only caller that passes one. Forwarded in `configure` below — lifting the
   * input without forwarding it is the failure the trait-forms spec guards.
   */
  public readonly uploadFrom = input<UploadOrigin>('skill_builder');
  /**
   * The compliance shared-store mechanism (Stage C): when the page binds a
   * store, all zones bound to the same instance render identical chips and gate
   * Save together. Unbound zones lazily create their own instance.
   */
  public readonly uploadStore = input<FileUploadV2Store>();
  /**
   * Analytics assistant context for upload events (Art IV, X).
   * Optional; bound from the trait-form parent or undefined.
   */
  public readonly assistantForAnalytics = input<
    AssistantForAnalytics | undefined
  >();
  /** Analytics accessPath/group context for upload events (KD-3/KD-20). Optional. */
  public readonly assistantAccessPath = input<AccessPath | undefined>();
  public readonly assistantAccessGroup = input<GroupForAnalytics | undefined>();
  public readonly assistantTeammate = input<TeammateForAnalytics | undefined>();
  /**
   * A message owned by the parent form — a FormGroup-level error — rendered in
   * this zone's error slot, which no template had ever shown one in (#1454).
   *
   * Named `external*` deliberately: `errorMessage` at the bottom of this class
   * is the private per-file translator, and an input by that name is a
   * duplicate class member that does not compile.
   */
  public readonly externalErrorMessage = input<string | undefined>();

  /** The resolved store — set in ngOnInit; a signal so the template reacts. */
  private readonly store = signal<FileUploadV2Store | null>(null);
  protected readonly resolvedStore = computed(() => this.store());

  public readonly isDisabled = signal(false);

  /** `accept` attribute for the hidden input, from the zone's extension list. */
  protected readonly acceptAttribute = computed(() =>
    this.fileSupportedExtensions()
      .map((type) => `.${type}`)
      .join(','),
  );

  /**
   * The exact accepted-extension list, on the picker button (SB-3515 KD-14).
   *
   * KD-14 collapsed `ERROR_FILE_TYPE` to family names and moved the exact list
   * to "the picker tooltip" — but on this surface no such tooltip was ever
   * built, so a builder user refused by "Only text, audio, PDF and Office files
   * are supported." had no way left to discover which extensions those are.
   *
   * Built from `fileSupportedExtensions()`, the same input `acceptAttribute`
   * reads, so it names THIS zone's list rather than the chat one — the trait
   * forms mount narrower zones (no images, md-only configuration slots) and a
   * shared constant would advertise files their picker refuses.
   *
   * Reuses the chat surface's approved `PROMPT_INPUT.TOOLTIP.SUPPORTED_FORMATS`
   * string (EN/FR) rather than minting builder copy: KD-16's cap wording is
   * still owed a PM/design sign-off and is deliberately not written here.
   */
  protected readonly supportedFormatsTooltip = computed(
    () =>
      this.translate.instant('PROMPT_INPUT.TOOLTIP.SUPPORTED_FORMATS', {
        extensions: this.fileSupportedExtensions().join(', '),
      }) as string,
  );

  /** Distinct translated messages for files this zone refused individually. */
  private readonly perFileErrors = computed<string[]>(() => {
    const store = this.store();
    if (!store) return [];
    const messages = new Set<string>();
    for (const file of Object.values(store.state().files)) {
      if (file.error !== null) {
        messages.add(this.errorMessage(file.error, file));
      }
    }
    return [...messages];
  });

  /**
   * Whether the parent's group-level message is the one on screen — i.e. no
   * file refused itself, and the parent supplied a message.
   */
  private readonly showsExternalError = computed<boolean>(
    () => this.perFileErrors().length === 0 && !!this.externalErrorMessage(),
  );

  /**
   * Whether a group-level error implicates the files THIS zone holds, so their
   * chips should carry the error border (Max, review on #1456: the border and
   * caption went red while the chips under them stayed plain, unlike a
   * per-file refusal which already reds its own chip).
   *
   * Gated on holding more than one file, because that is the only case the
   * zone can attribute to itself. `skillMdConfigurationFile` has two causes
   * and one error key: a wrong COUNT here, or a `skill.md` sitting in a
   * sibling zone. In the sibling case this zone may hold a perfectly valid
   * `skill.md` — reddening it would point at the one correct file and leave
   * the offending one clean, which is worse than leaving both plain.
   *
   * The cost is under-marking a single seeded file whose name is wrong: its
   * caption names it exactly ("The configuration file must be named
   * skill.md.") and staging refuses that name outright since #1457, so it is
   * reachable only for skills saved before that. Marking a correct file is the
   * error worth avoiding; missing a named one is not.
   */
  private readonly groupErrorMarksOwnFiles = computed<boolean>(() => {
    const store = this.store();
    if (!store || !this.showsExternalError()) return false;
    return Object.keys(store.state().files).length > 1;
  });

  /** Chips rendered from store state (loading/error styling by status). */
  protected readonly chips = computed<ZoneChip[]>(() => {
    const store = this.store();
    if (!store) return [];
    const groupError = this.groupErrorMarksOwnFiles();
    return Object.values(store.state().files).map((file) => ({
      id: file.uuid,
      name: file.name,
      // Edit-mode seeds can arrive with no extension (a document view that only
      // carried id/name); default rather than crash, mirroring the V1 chip.
      fileType: file.extension
        ? fromSeriousSystem.getFileTypeIconFromExtension(file.extension)
        : 'default',
      isLoading:
        file.status === 'upload-pending' || file.status === 'upload-ongoing',
      isError:
        file.status === 'upload-invalid' ||
        file.status === 'upload-failed' ||
        groupError,
    }));
  });

  /**
   * Distinct translated error messages for the current error chips, falling
   * back to the parent's group-level message.
   *
   * Per-file errors win: a refused file already explains itself, and the group
   * validator fires at the same instant — an invalid file never enters the
   * control value, so `configurationFiles.length !== 1` is true too — which
   * would otherwise stack two red captions for one action.
   *
   * The old `if (!store) return []` early return is gone on purpose: it would
   * swallow the parent's message during the window before `ngOnInit` resolves
   * the store.
   */
  protected readonly currentErrors = computed<string[]>(() => {
    const perFile = this.perFileErrors();
    if (perFile.length) {
      return perFile;
    }
    const external = this.externalErrorMessage();
    return external ? [external] : [];
  });

  private onChange: (value: AssistantBuilderFileValue[]) => void = () => {
    void 0;
  };
  private onTouched: () => void = () => {
    void 0;
  };
  private onValidatorChange: () => void = () => {
    void 0;
  };

  /** The persistable id set last emitted / reconciled (onChange gate). */
  private lastEmittedIds = '';
  /** Buffered writeValue seed when it fires before ngOnInit resolves the store. */
  private pendingSeed: AssistantBuilderFileValue[] | null = null;

  constructor() {
    // Store state → validators + onChange. Runs on every state mutation once the
    // store resolves. onChange fires only when the PERSISTABLE id set changes
    // (e.g. pick, remove, ongoing→failed drops an id from the value); pure
    // status transitions that keep the set (pending→ongoing→done) don't emit.
    effect(() => {
      const store = this.store();
      if (!store) return;
      store.state(); // track
      this.onValidatorChange();
      this.emitIfChanged(store);
    });
  }

  ngOnInit(): void {
    const store = this.uploadStore() ?? createFileUploadV2Store(this.injector);
    // Idempotent, overwrite semantics (S14): the three compliance slots each
    // configure the same page-owned store on mount.
    store.configure({
      supportedExtensions: this.fileSupportedExtensions(),
      transactionId: this.transactionId(),
      allowReservedSkillMdFilename: this.allowReservedSkillMdFilename(),
      uploadFrom: this.uploadFrom(),
      uploadAssistant: this.assistantForAnalytics(),
      uploadAccessPath: this.assistantAccessPath(),
      uploadGroup: this.assistantAccessGroup(),
      uploadTeammate: this.assistantTeammate(),
    });
    if (this.pendingSeed !== null) {
      this.reconcileInto(store, this.pendingSeed);
      this.pendingSeed = null;
    }
    // Set last so the reactive effect fires with the store fully seeded.
    this.store.set(store);
  }

  public onRemove(id: string): void {
    if (this.isDisabled()) return;
    this.store()?.removeFile(id);
  }

  /**
   * The resolved store instance — exposed for the compliance form's dev-mode
   * shared-store assertion (closed-loop invariant #2). Null before ngOnInit.
   */
  public getResolvedStore(): FileUploadV2Store | null {
    return this.store();
  }

  // --- ControlValueAccessor ---

  /**
   * Reconcile store to the incoming persistable value. Buffers when it fires
   * before ngOnInit (never creating a premature own store — which would split
   * the compliance shared-store topology).
   */
  writeValue(value: AssistantBuilderFileValue[] | null): void {
    const incoming = value ?? [];
    const store = this.store();
    if (!store) {
      this.pendingSeed = incoming;
      return;
    }
    this.reconcileInto(store, incoming);
  }

  registerOnChange(fn: (value: AssistantBuilderFileValue[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // --- Validator ---

  validate(): ValidationErrors | null {
    const store = this.store();
    if (!store) return null;
    const files = Object.values(store.state().files);
    const errors: ValidationErrors = {};
    if (
      files.some(
        (f) => f.status === 'upload-pending' || f.status === 'upload-ongoing',
      )
    ) {
      errors['uploadPending'] = true;
    }
    if (files.some((f) => f.status === 'upload-failed')) {
      errors['uploadFailed'] = true;
    }
    if (files.some((f) => f.status === 'upload-invalid')) {
      errors['uploadInvalid'] = true;
    }
    return Object.keys(errors).length ? errors : null;
  }
  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // --- internals ---

  /**
   * Persistable-only reconcile (Store-lifecycle decision #5). (i) remove only
   * **upload-done** entries the incoming value dropped — never in-flight
   * pending/ongoing entries (the store owns those; the form value transiently
   * lags a just-picked file, e.g. the compliance mirror patching a sibling
   * before the pick's onChange propagates — dropping them would abort a live
   * upload), and never error chips; (ii) insert missing ids via
   * seedPersistedFiles (never re-upload); (iii) an id already in the store is
   * left entirely untouched; (iv) never calls onChange and is a no-op when the
   * persistable id set already matches. The reactive effect re-emits onChange
   * to pull the form up to the store when they diverge.
   */
  private reconcileInto(
    store: FileUploadV2Store,
    incoming: AssistantBuilderFileValue[],
  ): void {
    const incomingIds = new Set(incoming.map((v) => v.id));
    const stateFiles = Object.values(store.state().files);
    const persistableIds = stateFiles.filter(isPersistable).map((f) => f.uuid);

    const targetKey = idKey([...incomingIds]);
    if (idKey(persistableIds) === targetKey) {
      // No-op: prevents mirror feedback loops (the compliance sync patches
      // siblings on every new array ref).
      this.lastEmittedIds = targetKey;
      return;
    }

    const toRemove = stateFiles
      .filter((f) => f.status === 'upload-done' && !incomingIds.has(f.uuid))
      .map((f) => f.uuid);
    if (toRemove.length) {
      store.removeFiles(toRemove);
    }

    const present = new Set(Object.keys(store.state().files));
    const toSeed = incoming
      .filter((v) => !present.has(v.id))
      .map((v) => ({
        uuid: v.id,
        name: v.name,
        extension: v.extension,
        size: v.size,
      }));
    if (toSeed.length) {
      store.seedPersistedFiles(toSeed);
    }

    // writeValue never calls onChange — just record the new persistable set so
    // the reactive effect doesn't echo it back.
    this.lastEmittedIds = targetKey;
  }

  private emitIfChanged(store: FileUploadV2Store): void {
    const persistable = Object.values(store.state().files).filter(
      isPersistable,
    );
    const key = idKey(persistable.map((f) => f.uuid));
    if (key === this.lastEmittedIds) return;
    this.lastEmittedIds = key;
    this.onChange(
      persistable.map((f) => ({
        id: f.uuid,
        name: f.name,
        extension: f.extension,
        size: f.size,
      })),
    );
    this.onTouched();
  }

  /**
   * Total error map (`Record<Exclude<FileError, null>, string>`): adding a new
   * FileError literal breaks the build here until it is mapped. The store's
   * English literals stay untouched (chat string-matching is load-bearing).
   */
  private errorMessage(
    error: Exclude<FileError, null>,
    file: Pick<FileUploadStateFile, 'name' | 'audioMaxDurationSeconds'>,
  ): string {
    const messages: Record<Exclude<FileError, null>, string> = {
      // KD-14: family names, not the extension list — post-T6 this printed 20
      // items on the builder surface. The exact list lives on this zone's
      // picker tooltip (`supportedFormatsTooltip` above), which is the
      // user-reachable source of truth QA asserts against. That tooltip did not
      // exist when this message was first shortened, so for one release the
      // list was reachable nowhere on the builder surface.
      'Invalid file type': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_TYPE',
      ) as string,
      // The EFFECTIVE ceiling for THIS file, never the tenant global
      // (SB-3515 KD-16/KD-17): a 26 MB skill-KB .log under a 50 MB tenant cap
      // is refused at 25 and must be told 25 — same shared helper the size
      // check itself reads, so message and gate cannot disagree.
      //
      // T7 adds the second half: the text case has its own approved wording
      // (spec §4 names the text family in the sentence), so it is a distinct
      // key rather than the same sentence with a different number.
      //
      // ⚠️ `cap` gained a third value in SB-3537, and unlike the chat twin this
      // branch can never see it: `xlsx` is absent from
      // `ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS`, so a workbook is
      // refused as `'Invalid file type'` — which precedes `'File size exceeded'`
      // in the one `else if` chain at `file-upload-v2.helpers.ts` — and never
      // reaches a size verdict on this surface. That is why no spec pins an
      // `'excel'` case here; the exclusion itself is pinned in
      // `libs/shared/src/lib/file-upload.spec.ts` instead.
      'File size exceeded': this.translate.instant(
        UploadValidators.effectiveUploadCap(file).cap === 'text_family'
          ? 'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_TEXT_SIZE'
          : 'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_SIZE',
        { size: effectiveUploadCapInMB(file) },
      ) as string,
      'Empty file': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_EMPTY',
      ) as string,
      // SB-3515 T25: the type is allowed, the NAME is reserved — deliberately
      // not the ERROR_FILE_TYPE copy, which would tell the user their .md is
      // unsupported when renaming the file is the actual fix.
      'Reserved filename': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_RESERVED_NAME',
      ) as string,
      // Config-zone near-miss (SB-3515 T25 follow-up): the OPPOSITE fix from
      // the reserved-name copy — here the file must be named exactly
      // skill.md, not renamed away from it. Mirrors the API's own 400.
      'Invalid configuration filename': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_CONFIG_FILENAME',
      ) as string,
      'Error uploading file': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_UPLOAD',
      ) as string,
      // SB-3474: reuses the shared audio copy rather than minting a second
      // string for the same fact. `audioMaxDurationSeconds` is always set
      // alongside this error — the preflight records the limit it compared
      // against precisely so the message can name it.
      'Audio too long': this.translate.instant('TOAST.WARNING.AUDIO_TOO_LONG', {
        fileName: file.name,
        maxMinutes: Math.round((file.audioMaxDurationSeconds ?? 0) / 60),
      }) as string,
    };
    return messages[error];
  }
}
